import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiArrowRight } from 'react-icons/fi';
import LocationVehiclePage from './LocationVehiclePage';

const seoConfig = {
  city: 'Denton',
  state: 'TX',
  slug: 'denton-tx',
  county: 'Denton County',
  title: 'Used Cars Denton TX - European Cars and Affordable Vehicles',
  h1: 'Used Cars Near Denton — European and Everyday Vehicles',
  metaDescription: 'Used European cars and everyday vehicles near Denton TX. BMW, Audi, VW, Mercedes — plus trucks and SUVs. 35 min on 380. Full service shop on site. (817) 382-2316.',
  ogTitle: 'European Cars & Used Vehicles Near Denton TX',
  ogDescription: 'Skip Denton\'s Auto Mile. 35 minutes on 380 for BMW, Audi, VW and everyday vehicles at prices that make sense.',
  ctaHeadline: 'One Road From Denton. No Tolls.',
  ctaSubline: 'US-380 West. 35 minutes. The same road you already know.',
  faqs: [
    {
      q: 'How do I get to Paradise-Performance from Denton?',
      a: 'Take US-380 West. One road, no turns, no tolls. About 35 minutes from the Denton Square. You\'ll pass Decatur and we\'re just past it in Paradise.'
    },
    {
      q: 'What European cars do you carry?',
      a: 'BMW, Audi, Mercedes-Benz, Volkswagen and Porsche. We also work on them in our shop — factory-level diagnostic equipment. Most European car owners in Denton drive to Fort Worth for service. You can come to us instead.'
    },
    {
      q: 'Do you finance first-time buyers?',
      a: 'Yes. Lots of our Denton buyers are students or recent grads purchasing their first vehicle. We work with lenders who approve first-time buyers, often without a co-signer.'
    },
    {
      q: 'Are your European cars cheaper than Denton dealerships?',
      a: 'Consistently. A used Audi A4 on Denton\'s Auto Mile carries metro overhead pricing. The same car from us reflects our actual cost — which is significantly lower because we\'re not paying an I-35 lease.'
    },
    {
      q: 'Can you service my BMW or Audi after I buy it?',
      a: 'Yes. We run a European car specialist shop on the same property. Diagnostics, maintenance, repairs — all in-house. No sending you to a dealership in Dallas for warranty work.'
    },
    {
      q: 'Do you sell regular cars too, or just European?',
      a: 'Both. We carry trucks, SUVs, sedans and golf carts alongside the European inventory. Whatever you need for daily life in Denton, we\'ve probably got it.'
    }
  ]
};

const DentonTX = () => (
  <LocationVehiclePage config={seoConfig}>

    {/* ─── Hero ─── */}
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 40% 50%, rgba(217,119,6,0.2) 0%, transparent 50%)'
        }} />
      </div>
      <div className="relative container mx-auto px-4">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Used Cars Near Denton — European and Everyday Vehicles
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl">
            BMW. Audi. VW. Mercedes. Plus trucks and family SUVs. 35 minutes on 380 saves you thousands over Denton's Auto Mile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/inventory"
              className="inline-flex items-center justify-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
            >
              View Inventory <FiArrowRight className="ml-2" />
            </Link>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
            >
              <FiPhone className="mr-2" /> (817) 382-2316
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Skip Denton's Markup ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Denton, TX</p>
          <h2 className="text-3xl font-bold mb-6">Denton's Auto Mile Charges You for the Address</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Denton has more dealerships per mile than most cities its size. The strip along I-35 and University Drive is packed with them. But competition hasn't driven prices down. It's driven marketing budgets up. You're paying for their billboards, not a better vehicle.</p>
            <p>We sit 35 minutes west on the same 380 you probably already drive. One road. No tolls. No cloverleaf merging. Most Denton buyers tell us the drive felt shorter than fighting through town to reach the opposite end of I-35.</p>
            <p>The difference at the end of that drive? Our prices are based on our actual costs — which are low, because county-road lots don't pay for I-35 frontage. A used Audi A4 here carries a fair price, not a padded one.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── European Car Focus ─── */}
    <section className="py-16 bg-gray-900 text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-400 font-semibold mb-2 text-center">Our Specialty</p>
          <h2 className="text-3xl font-bold text-center mb-4">European Cars — Sold and Serviced Here</h2>
          <p className="text-center text-gray-400 mb-10 max-w-2xl mx-auto">We don't just sell European vehicles. We work on them every day. Factory diagnostic equipment. Mechanics trained on German engineering.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">BMW</h3>
              <p className="text-gray-400">3 Series, 5 Series, X3, X5 — the lineup Denton drives. We carry pre-owned BMWs at prices that don't require a BMW income. Full diagnostic capability in our shop.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Audi</h3>
              <p className="text-gray-400">A4, A6, Q5. Quattro handles Texas weather better than people think. We stock well-maintained Audis and can diagnose every system in-house.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Volkswagen</h3>
              <p className="text-gray-400">Jetta, Tiguan, Golf. German engineering at a real-world price point. Popular with students and young professionals in Denton. Buy it here, maintain it here.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Mercedes-Benz</h3>
              <p className="text-gray-400">C-Class, E-Class, GLC. Pre-owned luxury that doesn't come with a certified pre-owned markup. Our shop handles all Mercedes service and diagnostics.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Porsche</h3>
              <p className="text-gray-400">Cayenne, Macan, the occasional 911. Not common on our lot, but when we get one, it goes fast. Factory-trained tools for diagnostics and service.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Service For All European</h3>
              <p className="text-gray-400">Already own a BMW or Audi? Don't drive to Dallas for service. Our <Link to="/services/european-car-repair" className="text-amber-400 hover:underline">European specialist shop</Link> handles maintenance and repairs right here.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The Student & Young Buyer Angle ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">College Town</p>
          <h2 className="text-3xl font-bold mb-6">Students Don't Need Dealership Markup</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>UNT and TWU put 50,000+ students in Denton. Most of them need a car. What they don't need is a $400/month payment from an Auto Mile lot that tacked on GAP and tire protection without asking.</p>
              <p>We carry reliable sedans and compacts under $10K. Cars that start every morning, get decent mileage and don't need repairs every semester. First-time buyer financing with no co-signer in many cases.</p>
              <p>If you're a parent sending a kid to Denton — we're the kind of dealer you want them going to. Fair price, no pressure, a warranty if something goes wrong.</p>
            </div>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Young professionals too. Denton's growing past its college-town roots. People working in tech, healthcare and education who want a nice vehicle without financing $35K for five years.</p>
              <p>A pre-owned Volkswagen Jetta or BMW 3 Series from us costs less than a new base-model Corolla. Think about that. German engineering, fewer miles than a rental fleet car, and you're paying less.</p>
              <p>That's the European car advantage at a rural-overhead dealership. <Link to="/financing" className="text-amber-600 hover:underline">Financing is available</Link> for every credit situation.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Denton County Areas ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2 text-center">Denton County</p>
          <h2 className="text-3xl font-bold text-center mb-10">Denton Areas We Serve</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">UNT / TWU Campus Area</h3>
              <p className="text-gray-600">Students and faculty. Need affordable, reliable transportation. Sedans and compacts under $10K move fast with this crowd.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Rayzor Ranch / South Denton</h3>
              <p className="text-gray-600">Growing commercial area. Young professionals moving into new apartments and townhomes. VW and Audi buyers tend to come from here.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Argyle / Northlake</h3>
              <p className="text-gray-600">Higher-end residential growth south of Denton. Families here want quality SUVs and European vehicles. Budget-conscious but not cheap.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Justin / Roanoke</h3>
              <p className="text-gray-600">Between Denton and Fort Worth on 114/35W. Growing suburbs with young families. Trucks and family SUVs sell well to buyers here.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Sanger / Krum</h3>
              <p className="text-gray-600">North of Denton. More rural, similar feel to our side of the county. Truck and trailer buyers. People who don't love the DFW dealership experience.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Robson Ranch</h3>
              <p className="text-gray-600">Active-adult community. Retirees who want a quality vehicle without the dealership runaround. European sedans and golf carts are popular here.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The 380 Drive ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center">The 380 Corridor — One Road to Better Prices</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg text-center">
            <p>US-380 runs straight from Denton through Decatur and into Paradise. No I-35 traffic. No tollway fees. No merging across four lanes to take an exit.</p>
            <p>35 minutes of easy driving. Open fields. Speed limit stays up. Most Denton buyers say the trip felt shorter than crossing their own city during rush hour.</p>
            <p>And the payoff at the end? Lower prices, a real service shop and a team that doesn't treat you like a number. <Link to="/used-cars-decatur-tx" className="text-amber-600 hover:underline">Decatur buyers</Link> figured this out a while ago. Now Denton's catching on.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Cross-Links ─── */}
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Also Serving These Areas</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/used-cars-decatur-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Decatur</h3>
              <p className="text-gray-500 text-sm">County seat. 10 min from our lot.</p>
            </Link>
            <Link to="/used-cars-bridgeport-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Bridgeport</h3>
              <p className="text-gray-500 text-sm">Lake trucks and golf carts. 6 min.</p>
            </Link>
            <Link to="/used-cars-wise-county" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Wise County</h3>
              <p className="text-gray-500 text-sm">Our home base. Every town covered.</p>
            </Link>
            <Link to="/used-cars-weatherford-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Weatherford</h3>
              <p className="text-gray-500 text-sm">Parker County. 30 min north of I-20.</p>
            </Link>
          </div>
        </div>
      </div>
    </section>

  </LocationVehiclePage>
);

export default DentonTX;
